import { useAuth0 } from "@auth0/auth0-react";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import BarChart from "@mui/icons-material/BarChart";
import BarChartRounded from "@mui/icons-material/BarChartRounded";
import Newspaper from "@mui/icons-material/Newspaper";
import PermMedia from "@mui/icons-material/PermMedia";
import Send from "@mui/icons-material/Send";
import { Box, Button, Card, Link, Skeleton, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { useDialog } from "../../../utils/hooks/use-dialog";
import { GradientIcon } from "../../generic/components/gradient-icon";
import { SeverityPill } from "../../generic/components/severity-pill";
import { getFirstName } from "../get-first-name";
import { useAssetCount } from "../hooks/use-asset-count";
import { useCreateSurveyHref } from "../hooks/use-create-survey-href";
import { useNewSurveyResults } from "../hooks/use-new-survey-results";
import { useSentMomentCounts } from "../hooks/use-sent-moment-counts";
import { useSurveyCount } from "../hooks/use-survey-count";
import { CreateButton } from "./create-button";
import { CreateWithAiDialog } from "./create-with-ai-dialog";
import { NewInsightsCount } from "./new-insights-count";
import { PendingApprovalsCount } from "./pending-approvals-count";
import { UnderConstructionDialog } from "./under-construction-dialog";
import { UnreadInboxCount } from "./unread-inbox-count";

import type { RecentlyEditedType } from "../hooks/use-recently-edited";
import type { Theme } from "@mui/material";
import type { FC, ReactNode } from "react";

const avenirFontFamily = (theme: Theme) => `"Avenir Next", ${theme.typography.fontFamily}`;

const Stat: FC<{
  icon: ReactNode;
  label: string;
  value: ReactNode;
  badge?: string;
  /** Shown in place of the value and badge, for when there is nothing to count yet. */
  emptyAction?: ReactNode;
  /** Where the stat leads. Not while it shows its emptyAction, which is a link of its own. */
  href?: string;
}> = ({ icon, label, value, badge, emptyAction, href }) => {
  const content = (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary" }}>
        <GradientIcon sx={{ "& .MuiSvgIcon-root": { fontSize: 16 } }}>{icon}</GradientIcon>
        <Typography variant="overline" sx={{ lineHeight: 1.5 }}>
          {label}
        </Typography>
      </Box>
      {/* The same height and width whether loading, counted or empty, so nothing shifts on load. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 36, minWidth: 140 }}>
        {emptyAction ?? (
          <>
            <Typography variant="h6" sx={{ color: "text.secondary" }}>
              {value}
            </Typography>
            {badge && (
              <SeverityPill
                color="success"
                variant="soft"
                sx={{ textTransform: "none", gap: 0.75 }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "success.main",
                    flexShrink: 0,
                  }}
                />
                <Typography variant="caption" component="span">
                  {badge}
                </Typography>
              </SeverityPill>
            )}
          </>
        )}
      </Box>
    </>
  );

  return href && !emptyAction ? (
    <Link
      component={RouterLink}
      to={href}
      color="inherit"
      underline="none"
      sx={{ display: "block" }}
    >
      {content}
    </Link>
  ) : (
    <Box>{content}</Box>
  );
};

/** What to do instead, when a stat has nothing to count. */
const EmptyStatAction: FC<{ label: string; href?: string; onClick?: () => void }> = ({
  label,
  href,
  onClick,
}) => (
  <Button
    href={href}
    onClick={onClick}
    data-analytics-id={`dashboard-empty-stat-${label.toLowerCase().replaceAll(" ", "-")}`}
    sx={{ px: 0, minWidth: 0, fontWeight: 700, "&:hover": { background: "none" } }}
  >
    {label}
  </Button>
);

const SentStat: FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const { data, error } = useSentMomentCounts(refreshKey);

  if (error) return <Stat icon={<Send />} label="Sent" value="–" href="/moments/history" />;

  return (
    <Stat
      icon={<Send />}
      label="Sent"
      href="/moments/history"
      value={data ? data.sent.toLocaleString() : <Skeleton width={48} />}
      badge={data ? `${data.active.toLocaleString()} active` : undefined}
      emptyAction={
        data?.sent === 0 ? (
          <EmptyStatAction label="Send a Communication" href="/moments" />
        ) : undefined
      }
    />
  );
};

const SurveysStat: FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const { data, error } = useSurveyCount(refreshKey);
  const { data: hasNewResults } = useNewSurveyResults();
  const createSurveyHref = useCreateSurveyHref();

  return (
    <Stat
      icon={<BarChart />}
      label="Surveys"
      href="/listen"
      value={error ? "–" : data === null ? <Skeleton width={32} /> : data.toLocaleString()}
      badge={hasNewResults ? "New results" : undefined}
      emptyAction={
        data === 0 ? <EmptyStatAction label="Create a survey" href={createSurveyHref} /> : undefined
      }
    />
  );
};

const AssetsStat: FC<{ refreshKey: number; onCreateDesign: () => void }> = ({
  refreshKey,
  onCreateDesign,
}) => {
  const { data, error } = useAssetCount(refreshKey);

  return (
    <Stat
      icon={<PermMedia />}
      label="Assets"
      href="/content-library"
      value={error ? "–" : data === null ? <Skeleton width={32} /> : data.toLocaleString()}
      emptyAction={
        data === 0 ? (
          <EmptyStatAction label="Create a design" onClick={onCreateDesign} />
        ) : undefined
      }
    />
  );
};

const createOptions = [
  { label: "With AI", icon: <AutoAwesome />, withAi: true },
  { label: "Communication", icon: <Send />, href: "/moments" },
  { label: "Newsletter", icon: <Newspaper />, href: "/moments/newsletter" },
];

export const DashboardHeader: FC<{
  /** Each kind's count is counted again when its key changes. */
  refreshKeys: Record<RecentlyEditedType, number>;
}> = ({ refreshKeys }) => {
  const { user } = useAuth0();
  const firstName = getFirstName(user);
  const underConstructionDialog = useDialog();
  const withAiDialog = useDialog();
  const createSurveyHref = useCreateSurveyHref();

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
        gap: 3,
        alignItems: "end",
      }}
    >
      <Stack spacing={2}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
          <Typography
            variant="h5"
            sx={{ fontFamily: avenirFontFamily, fontWeight: 600, fontSize: "21px" }}
          >
            Hello{firstName ? `, ${firstName}` : ""}
          </Typography>
          <UnreadInboxCount />
          <NewInsightsCount />
          <PendingApprovalsCount />
        </Box>
        <Card sx={{ display: "flex", gap: 4, px: 2, py: 1.5, width: "fit-content" }}>
          <SentStat refreshKey={refreshKeys.communication} />
          <SurveysStat refreshKey={refreshKeys.survey} />
          <AssetsStat
            refreshKey={refreshKeys.digital_asset}
            onCreateDesign={() => underConstructionDialog.handleOpen()}
          />
        </Card>
      </Stack>
      <Box>
        <Typography
          variant="h6"
          sx={{ mb: 1, fontFamily: avenirFontFamily, fontWeight: 600, fontSize: "16px" }}
        >
          Create:
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {[
            ...createOptions,
            { label: "Survey", icon: <BarChartRounded />, href: createSurveyHref },
          ].map(({ label, icon, href, withAi }) => (
            <CreateButton
              key={label}
              label={label}
              icon={icon}
              href={href}
              onClick={withAi ? () => withAiDialog.handleOpen() : undefined}
              analyticsId={`dashboard-create-${label.toLowerCase().replaceAll(" ", "-")}`}
            />
          ))}
        </Box>
      </Box>
      <UnderConstructionDialog dialog={underConstructionDialog} />
      <CreateWithAiDialog dialog={withAiDialog} />
    </Box>
  );
};
